import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { Field, Form, Formik } from 'formik';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import styled, { keyframes } from 'styled-components';

import { ReactComponent as EyeOn } from '../../../assets/images/svg/eye-on.svg';
import { ReactComponent as EyeOff } from '../../../assets/images/svg/eye-off.svg';
import { ReactComponent as Lock } from '../../../assets/images/svg/lock.svg';
import { ReactComponent as InfoIcon } from '../../../assets/images/svg/info-filled-blue.svg';
import { ReactComponent as AmberAlertTriangle } from '../../../assets/images/svg/amber-alert-triangle.svg';
import { ReactComponent as Close } from '../../../assets/images/svg/close.svg';
import BottomDrawer from '../../Drawer/Drawer';
import { ModalClose } from '../../Modal/Modal';
import SfhPrimaryButton from './SfhPrimaryButton';
import SfhSecondaryButton from './SfhSecondaryButton';
import SectionLoader from '../../Loader/SectionLoader';
import { hashedMask, isPositiveInteger } from '../../../util/helpers/ssnHelper';
import themeColors from '../../../assets/styles/themeColors';
import TextField from '../../InputFields/TextField';
import { InputLabel } from '../../InputFields/Components/InputComponents';
import FormContainer from '../../FormContainer/FormContainer';
import { ErrorMessage } from '../../InputFields/ControlledInput';
import Anchor from '../../Anchor/Anchor';
import { postUserSsn } from '../../../store/actions/user/userActions';
import { fetchAccountSettings } from '../../../store/actions/account/accountActions';
import { getErrorCodeFromAPIResponse } from '../../../util/helpers/rejectErrorCodeHelper';
import { FullSsnOrderType } from '../../../util/enum/api/fullSsnOrderTypes';
import { mediaBelow } from '../../../assets/styles/helper';
import { variables } from '../../../assets/styles/variables';
import useWindowSize from '../../../util/hooks/useIsMobileHook';

const IconButton = styled.button`
  outline: none;
  width: auto;
  height: auto;
  padding: 0;
  margin: 0;
  border: none;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const DrawerHero = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding-top: 4px;
  margin-bottom: 4px;
`;

const DrawerBody = styled.div`
  position: relative;
  width: 100%;
`;

const IrsDrawerClose = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 1;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 20px;
    height: 20px;
    display: block;
    color: ${themeColors.colorTextPrimary};
  }
`;

const DrawerIconWrapper = styled.div`
  margin: 16px 0;

  svg {
    width: 35px;
    height: 35px;
    display: block;
  }

  ${mediaBelow(variables.breakpoints.bpMd)} {
    svg {
      width: 44px;
      height: 44px;
    }
  }
`;

const DrawerTitle = styled.h2`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: ${themeColors.colorTextPrimary};
  text-align: center;
  margin: 0 0 16px;
  padding: 0 8px;
  line-height: 1.35;

  ${mediaBelow(variables.breakpoints.bpMd)} {
    font-size: 16px;
  }
`;

const NoteBoxIndividualJoint = styled.div`
  padding: 12px;
  color: ${themeColors.colorBlack};
  background-color: ${themeColors.colorLightYellow};
  border: 1px solid ${themeColors.colorRowBrThree};
  border-radius: 4px;
  margin-bottom: 16px;
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 14px;
  line-height: 19px;

  ${mediaBelow(variables.breakpoints.bpMd)} {
    padding: 6px 10px;
    font-size: 14px;
    line-height: 19px;
    margin-bottom: 16px;
  }
`;

const NoteBoxTrust = styled.div`
  padding: 12px;
  color: ${themeColors.colorBlack};
  background-color: ${themeColors.colorLightYellow};
  border: 1px solid ${themeColors.colorRowBrThree};
  border-radius: 4px;
  margin-bottom: 16px;
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: 14px;
  line-height: 19px;

  ${mediaBelow(variables.breakpoints.bpMd)} {
    padding: 6px 10px;
    font-size: 14px;
    line-height: 19px;
  }
`;

/** Extends InputLabel so 14px wins over the shared 16px rule (nested selectors lose injection order). */
const SfhSsnInputLabel = styled(InputLabel)`
  font-size: 14px;
  line-height: 19px;
  font-weight: 400;

  ${mediaBelow(variables.breakpoints.bpMd)} {
    font-size: 14px;
    line-height: 19px;
  }
`;

const SfhSsnFieldLabelWrap = styled.div`
  margin-bottom: 4px;
`;

const Container = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;

  button {
    position: absolute;
    right: 11px;
    bottom: 13px;

    ${mediaBelow(variables.breakpoints.bpMd)} {
      bottom: 7px;
    }
  }

  svg {
    width: 24px;
    height: 24px;
    color: ${themeColors.colorInputPlaceholder};
  }

  input {
    padding-right: 42px;
    font-size: 14px;
    line-height: 1.4;

    &::placeholder {
      font-size: 14px;
      color: ${themeColors.colorTextSecondary};
      font-weight: 400;
    }
    ${mediaBelow(variables.breakpoints.bpMd)} {
      && {
        height: 38px;
      }
    }
    ${mediaBelow(variables.breakpoints.bpXl)} {
      font-size: 14px;

      &::placeholder {
        font-size: 14px;
      }
    }
  }
`;

const InfoSecureContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-top: 8px;
  ${mediaBelow(variables.breakpoints.bpMd)} {
    margin-top: 6px;
    margin-bottom: 16px;
  }
`;

const LockIconWrap = styled.div`
  height: 16px;
  width: 16px;
  margin-right: 4px;
`;

const InfoSecure = styled.span`
  font-size: 14px;
  color: ${themeColors.colorTextSecondary};
  ${mediaBelow(variables.breakpoints.bpMd)} {
    font-size: 14px;
    font-weight: 500;
  }
`;

const FormActions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
  margin-top: 16px;
`;

const FooterActions = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  padding: 0 16px 16px;
  margin: -16px 0;
  width: 100%;
`;

const MobileFormActions = styled(FormActions)`
  display: none;

  ${mediaBelow(variables.breakpoints.bpMd)} {
    display: flex;
  }
`;

const IrsButtonRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 12px;
  width: 100%;

  button {
    font-family: ${variables.family.goldSilver}, sans-serif;
    font-size: 16px;
    height: 44px;
    width: 100%;
  }

  ${mediaBelow(variables.breakpoints.bpMd)} {
    grid-template-columns: 1fr;
    grid-gap: 14px;

    button {
      font-size: 16px;
      font-weight: 600;
      height: 44px;
      width: 100%;
      min-width: 0;
    }

    button:nth-child(1) {
      order: 2;
    }

    button:nth-child(2) {
      order: 1;
    }
  }
`;

const LearnMoreLinkWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 16px;
`;

export const LearnMoreLinkText = styled.div`
  font-size: 14px;
  text-decoration: underline;
  text-decoration-style: solid;
  color: ${themeColors.colorLinkPrimary};
`;

const toastSlideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const IRS_DRAWER_MAX_HEIGHT = '85%';

/**
 * Desktop: anchored to Sell From Home quantity `SectionLoader` / `LoaderContainer`
 * (position: relative), i.e. the modal body column — not the viewport.
 */
const IrsWarningToastRoot = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 60;
  pointer-events: none;
  box-sizing: border-box;
  padding: 24px 16px 0;
`;

/** Mobile: full-viewport top stack (portaled to `document.body`). */
const IrsWarningToastRootMobile = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 60;
  pointer-events: none;
  box-sizing: border-box;
  padding: 16px;
`;

const IrsWarningToastStack = styled.div`
  pointer-events: none;
  display: flex;
  flex-direction: column;
  gap: 12px;
  box-sizing: border-box;
  align-items: stretch;
  width: 100%;
`;

const IrsWarningToastCard = styled.div`
  pointer-events: auto;
  position: relative;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  border-radius: ${variables.borderRadius.borderRadiusMd};
  background-color: ${themeColors.colorWhite};
  box-shadow: 0 3px 8px 0 rgba(0, 0, 0, 0.24);
  overflow: hidden;
  animation: ${toastSlideIn} 0.25s ease-out;
`;

const IrsWarningToastStripe = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 6px;
  border-radius: 4px 0 0 4px;
  background-color: ${themeColors.colorWarning};
`;

const IrsWarningToastRow = styled.div`
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px 40px 16px 16px;
`;

const IrsWarningToastIconWrap = styled.div`
  flex-shrink: 0;
  padding-top: 2px;

  svg {
    width: 24px;
    height: 24px;
    display: block;
  }
`;

const IrsWarningToastCopy = styled.div`
  flex: 1;
  min-width: 0;
  font-family: ${variables.family.goldSilver}, sans-serif;
  padding-right: 4px;
`;

const IrsWarningToastTitle = styled.p`
  margin: 0 0 4px;
  font-size: 16px;
  font-weight: 600;
  color: ${themeColors.colorTextPrimary};
  line-height: 1.35;
`;

const IrsWarningToastMessage = styled.p`
  margin: 0;
  font-size: 14px;
  font-weight: 400;
  color: ${themeColors.colorTextSecondary};
  line-height: 1.5;
`;

/** Same control + SVG sizing as SFH wizard `ModalClose` (SellFromHomeWizard header). */
const IrsToastModalClose = styled(ModalClose)`
  position: absolute;
  top: 12px;
  right: 12px;
`;

const TOP_TOAST_AUTO_CLOSE_MS = 15000;
const MAX_IRS_TOASTS = 2;

const FORM_ID = 'sfh-irs-ssn-form';
const SSN_TOO_MANY_ATTEMPTS = 'SSNTooManyAttempts';

const SellFromHomeIrsSsnDrawer = ({
  isOpen,
  onClose,
  onSuccess,
  onLockedOut,
  copy,
  accountUid,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [actualSsn, setActualSsn] = useState('');
  const [isVisible, setIsVisible] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [topToasts, setTopToasts] = useState([]);
  const [isLockoutPending, setIsLockoutPending] = useState(false);
  const toastTimersRef = useRef(new Map());
  // Optional per-toast onDismiss callbacks. Fired exactly once when the toast
  // is removed (auto-close, user click, or drawer close via clearAllToasts).
  const toastDismissRef = useRef(new Map());

  const maskedSsn = useMemo(() => hashedMask(actualSsn, true, isVisible), [
    actualSsn,
    isVisible,
  ]);

  const removeToast = useCallback((toastId) => {
    const timers = toastTimersRef.current;
    const tid = timers.get(toastId);
    if (tid != null) {
      window.clearTimeout(tid);
      timers.delete(toastId);
    }
    const dismissals = toastDismissRef.current;
    const onDismiss = dismissals.get(toastId);
    dismissals.delete(toastId);
    setTopToasts((prev) => prev.filter((x) => x.id !== toastId));
    if (typeof onDismiss === 'function') {
      onDismiss();
    }
  }, []);

  const clearAllToasts = useCallback(() => {
    const timers = toastTimersRef.current;
    timers.forEach((tid) => window.clearTimeout(tid));
    timers.clear();
    const dismissals = toastDismissRef.current;
    const pending = Array.from(dismissals.values());
    dismissals.clear();
    setTopToasts([]);
    pending.forEach((cb) => {
      if (typeof cb === 'function') cb();
    });
  }, []);

  const enqueueToast = useCallback(
    ({ title, message, onDismiss }) => {
      const id = `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
      const timers = toastTimersRef.current;
      const dismissals = toastDismissRef.current;
      setTopToasts((prev) => {
        const next = [{ id, title, message }, ...prev];
        if (next.length <= MAX_IRS_TOASTS) {
          return next;
        }
        next.slice(MAX_IRS_TOASTS).forEach((toast) => {
          const tid = timers.get(toast.id);
          if (tid != null) {
            window.clearTimeout(tid);
            timers.delete(toast.id);
          }
          dismissals.delete(toast.id);
        });
        return next.slice(0, MAX_IRS_TOASTS);
      });
      if (typeof onDismiss === 'function') {
        dismissals.set(id, onDismiss);
      }
      const tid = window.setTimeout(() => {
        removeToast(id);
      }, TOP_TOAST_AUTO_CLOSE_MS);
      timers.set(id, tid);
    },
    [removeToast],
  );

  useEffect(() => {
    if (!isOpen) {
      setActualSsn('');
      setIsVisible(false);
      setError('');
      setIsLockoutPending(false);
      clearAllToasts();
    }
  }, [isOpen, clearAllToasts]);

  useEffect(() => {
    const timers = toastTimersRef.current;
    return () => {
      timers.forEach((tid) => window.clearTimeout(tid));
      timers.clear();
    };
  }, []);

  const { isTrust, ssnAccountType, primaryHolderDisplayName } = copy;

  // Individual, joint, and trust all use the same holder-name copy per US tax regs.
  const noteBody = useMemo(() => {
    if (primaryHolderDisplayName) {
      return (
        <>
          {t('irs.ssn.primaryHolderNote1')}
          <strong>{primaryHolderDisplayName} </strong>
          {t('irs.ssn.primaryHolderNote2')}
        </>
      );
    }
    return <>{t('irs.ssn.note')}</>;
  }, [t, primaryHolderDisplayName]);

  const NoteBox = isTrust ? NoteBoxTrust : NoteBoxIndividualJoint;

  const handleSubmit = useCallback(() => {
    if (actualSsn === '') return;

    const formatted = `${actualSsn.slice(0, 3)}-${actualSsn.slice(
      3,
      5,
    )}-${actualSsn.slice(5, 9)}`;

    if (formatted.length < 11) {
      setError(
        isTrust
          ? t('fields.errors.validation.tinNotValid')
          : t('fields.errors.validation.ssnNotValid'),
      );
      return;
    }

    if (
      !isPositiveInteger(formatted.slice(0, 3)) ||
      !isPositiveInteger(formatted.slice(4, 6)) ||
      !isPositiveInteger(formatted.slice(7, 11))
    ) {
      setError(
        isTrust
          ? t('fields.errors.validation.tinDigits')
          : t('fields.errors.validation.ssnDigits'),
      );
      return;
    }

    if (!accountUid) return;

    const payload = {
      data: {
        AccountUid: accountUid,
        Tin: formatted,
        FullSsn: formatted,
        AccountType: ssnAccountType,
        // Tells BE this submission originated from the Sell From Home flow
        // (standard Sell defaults to `FullSsnOrderType.Sell` on the BE side).
        OrderType: FullSsnOrderType.SellFromHome,
      },
      setIsLoading,
      onSuccess: () => {
        dispatch(fetchAccountSettings({ accountUid }));
        onSuccess();
      },
      onError: (apiError) => {
        const errorCode = getErrorCodeFromAPIResponse(apiError);
        const isTooManyAttempts = errorCode === SSN_TOO_MANY_ATTEMPTS;

        if (isTooManyAttempts) {
          // On lockout, surface the toast and block further submits. Only
          // after the user (or the auto-close timer) dismisses the toast do
          // we refresh the settings API so `selectIsSellFromHomeRestricted`
          // picks up the lock. We intentionally skip `accounts/{accountUid}`
          // here — settings is the source of truth for the SFH restriction.
          setIsLockoutPending(true);
          enqueueToast({
            title: t(
              isTrust
                ? `irs.tin.error.${SSN_TOO_MANY_ATTEMPTS}.title`
                : `irs.ssn.error.${SSN_TOO_MANY_ATTEMPTS}.title`,
            ),
            message: t(
              isTrust
                ? `irs.tin.error.${SSN_TOO_MANY_ATTEMPTS}.message`
                : `irs.ssn.error.${SSN_TOO_MANY_ATTEMPTS}.message`,
            ),
            onDismiss: () => {
              dispatch(fetchAccountSettings({ accountUid }));
              if (typeof onLockedOut === 'function') {
                onLockedOut();
              }
            },
          });
          return;
        }

        if (errorCode) {
          enqueueToast({
            title: t(
              isTrust
                ? `irs.tin.error.${errorCode}.title`
                : `irs.ssn.error.${errorCode}.title`,
            ),
            message: t(
              isTrust
                ? `irs.tin.error.${errorCode}.message`
                : `irs.ssn.error.${errorCode}.message`,
            ),
          });
        }
      },
    };
    dispatch(postUserSsn(payload));
  }, [
    actualSsn,
    isTrust,
    t,
    accountUid,
    ssnAccountType,
    dispatch,
    onSuccess,
    onLockedOut,
    enqueueToast,
  ]);

  const handleChange = useCallback(
    (event) => {
      // While a too-many-attempts lockout toast is pending acknowledgement we
      // freeze the input so a stray keystroke doesn't auto-dismiss the toast
      // (and therefore fire the refresh / lockout flow) before the user has
      // had a chance to read it.
      if (isLockoutPending) return;

      const newValue = event.target.value;
      if (newValue.length > 11) return;

      const prevValue = maskedSsn;
      let next = actualSsn;

      if (newValue.length > prevValue.length) {
        next = `${actualSsn}${newValue.slice(-1)}`;
      } else {
        const charsRemovedCount = prevValue.length - newValue.length;
        next = actualSsn.slice(0, actualSsn.length - charsRemovedCount);
      }

      setError('');
      clearAllToasts();
      setActualSsn(next);
    },
    [actualSsn, maskedSsn, clearAllToasts, isLockoutPending],
  );

  const { width } = useWindowSize();
  const isMobileDrawer =
    (width ?? Number(variables.breakpoints.bpLg)) <
    Number(variables.breakpoints.bpMd);

  const toggleVisibility = useCallback(() => setIsVisible((v) => !v), []);

  const drawerActionButtons = (
    <IrsButtonRow>
      <SfhSecondaryButton onClick={onClose} data-cy="button-sfh-irs-close">
        {t('common.close')}
      </SfhSecondaryButton>
      <SfhPrimaryButton
        type="submit"
        form={isMobileDrawer ? undefined : FORM_ID}
        disabled={maskedSsn?.length !== 11 || isLoading || isLockoutPending}
        data-cy="button-sfh-irs-agree"
      >
        {t('common.agreeAndContinue')}
      </SfhPrimaryButton>
    </IrsButtonRow>
  );

  const drawerLearnMore = (
    <LearnMoreLinkWrap>
      <LearnMoreLinkText>
        <Anchor
          bold
          type="website"
          value={copy.learnMoreHref}
          text={copy.learnMoreText}
        />
      </LearnMoreLinkText>
    </LearnMoreLinkWrap>
  );

  const footer = !isMobileDrawer ? (
    <FooterActions>
      {drawerActionButtons}
      {drawerLearnMore}
    </FooterActions>
  ) : null;

  const ToastRoot = isMobileDrawer
    ? IrsWarningToastRootMobile
    : IrsWarningToastRoot;

  const topToastLayer = isOpen && topToasts.length > 0 && (
    <ToastRoot data-cy="sfh-irs-top-toast-stack">
      <IrsWarningToastStack>
        {topToasts.map((toast) => (
          <IrsWarningToastCard
            key={toast.id}
            role="alert"
            data-cy="sfh-irs-top-toast"
            data-toast-id={toast.id}
          >
            <IrsWarningToastStripe aria-hidden />
            <IrsWarningToastRow>
              <IrsWarningToastIconWrap aria-hidden>
                <AmberAlertTriangle />
              </IrsWarningToastIconWrap>
              <IrsWarningToastCopy>
                <IrsWarningToastTitle>{toast.title}</IrsWarningToastTitle>
                <IrsWarningToastMessage>{toast.message}</IrsWarningToastMessage>
              </IrsWarningToastCopy>
            </IrsWarningToastRow>
            <IrsToastModalClose
              type="button"
              aria-label={t('common.close')}
              onClick={() => removeToast(toast.id)}
              data-cy="sfh-irs-top-toast-close"
            >
              <Close />
            </IrsToastModalClose>
          </IrsWarningToastCard>
        ))}
      </IrsWarningToastStack>
    </ToastRoot>
  );

  const renderedTopToastLayer =
    isMobileDrawer && topToastLayer
      ? ReactDOM.createPortal(topToastLayer, document.body)
      : topToastLayer;

  return (
    <>
      {renderedTopToastLayer}
      <BottomDrawer
        isOpen={isOpen}
        onClose={onClose}
        hideHeader
        hideClose
        title=""
        footer={footer}
        maxHeight={IRS_DRAWER_MAX_HEIGHT}
      >
        <DrawerBody>
          <IrsDrawerClose
            type="button"
            aria-label={t('common.close')}
            onClick={onClose}
            data-cy="button-sfh-irs-drawer-close"
          >
            <Close />
          </IrsDrawerClose>
          <SectionLoader isLoading={isLoading}>
            <DrawerHero>
              <DrawerIconWrapper>
                <InfoIcon aria-hidden />
              </DrawerIconWrapper>
              <DrawerTitle>{copy.title}</DrawerTitle>
            </DrawerHero>

            <NoteBox>
              <strong>{t('common.important')}</strong>
              {noteBody}
            </NoteBox>

            <Formik
              initialValues={{ Ssn: '' }}
              onSubmit={handleSubmit}
              validateOnBlur
            >
              {() => (
                <Form id={FORM_ID}>
                  <SfhSsnFieldLabelWrap>
                    <SfhSsnInputLabel htmlFor="Ssn">
                      {copy.inputLabel}
                    </SfhSsnInputLabel>
                  </SfhSsnFieldLabelWrap>
                  <FormContainer hasError>
                    <Container>
                      <Field
                        component={TextField}
                        name="Ssn"
                        placeholder={copy.inputPlaceholder}
                        color={themeColors.colorBlack}
                        value={maskedSsn}
                        onChange={handleChange}
                        autocomplete="off"
                        hideError
                      />
                      <IconButton
                        type="button"
                        title={isVisible ? 'Hide SSN' : 'Show SSN'}
                        onClick={toggleVisibility}
                      >
                        {isVisible ? <EyeOn /> : <EyeOff />}
                      </IconButton>
                    </Container>
                  </FormContainer>
                  {error && (
                    <ErrorMessage error={error} name="Ssn">
                      {error}
                    </ErrorMessage>
                  )}
                  <InfoSecureContainer>
                    <LockIconWrap>
                      <Lock />
                    </LockIconWrap>
                    <InfoSecure>{copy.secureFootnote}</InfoSecure>
                  </InfoSecureContainer>
                  <MobileFormActions>
                    {drawerActionButtons}
                    {drawerLearnMore}
                  </MobileFormActions>
                </Form>
              )}
            </Formik>
          </SectionLoader>
        </DrawerBody>
      </BottomDrawer>
    </>
  );
};

SellFromHomeIrsSsnDrawer.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  onLockedOut: PropTypes.func,
  accountUid: PropTypes.string,
  copy: PropTypes.shape({
    title: PropTypes.string.isRequired,
    learnMoreHref: PropTypes.string.isRequired,
    learnMoreText: PropTypes.string.isRequired,
    secureFootnote: PropTypes.string.isRequired,
    inputLabel: PropTypes.string.isRequired,
    inputPlaceholder: PropTypes.string.isRequired,
    isTrust: PropTypes.bool,
    primaryHolderDisplayName: PropTypes.string,
    ssnAccountType: PropTypes.string,
  }).isRequired,
};

export default SellFromHomeIrsSsnDrawer;
